/**
 * Schema definitions for built-in property forms (Rear, Right, Left, Front)
 * Extends the existing FieldSchema system to support photo fields and built-in form structures
 */

import { FieldSchema } from './formSchema';

// Field types specific to property forms
export type PropertyFieldType = 
  | 'photo'           // Single or multiple photos
  | 'image'           // Alias for photo (used by custom fields)
  | 'text'            // Text input
  | 'textarea'        // Multi-line text
  | 'dropdown'        // Select dropdown
  | 'boolean'         // Yes/No/NA buttons
  | 'number'          // Numeric input
  | 'date';           // Date picker

// Photo field configuration
export interface PhotoFieldSchema {
  id: string;
  type: 'photo' | 'image';   // Support both 'photo' (built-in) and 'image' (custom fields)
  label: string;
  required?: boolean;
  maxPhotos?: number;        // Default 999 for multi, 1 for single
  helpText?: string;         // Optional hint text
  conditionalOn?: {          // Only show if another field has specific value
    fieldId: string;
    value: boolean | string | number;
  };
}

// Text field configuration
export interface TextFieldConfig {
  id: string;
  type: 'text';
  label: string;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  helpText?: string;
  conditionalOn?: {
    fieldId: string;
    value: boolean | string | number;
  };
}

// Textarea field configuration
export interface TextAreaFieldConfig {
  id: string;
  type: 'textarea';
  label: string;
  required?: boolean;
  placeholder?: string;
  rows?: number;
  helpText?: string;
  conditionalOn?: {
    fieldId: string;
    value: boolean | string | number;
  };
}

// Dropdown field configuration
export interface DropdownFieldConfig {
  id: string;
  type: 'dropdown';
  label: string;
  required?: boolean;
  options: Array<{ value: string; label: string }>;
  helpText?: string;
  conditionalOn?: {
    fieldId: string;
    value: boolean | string | number;
  };
}

// Boolean field configuration (Yes/No/NA buttons)
export interface BooleanFieldConfig {
  id: string;
  type: 'boolean';
  label: string;
  required?: boolean;
  style?: 'yes-no' | 'yes-no-na';
  helpText?: string;
  conditionalOn?: {
    fieldId: string;
    value: boolean | string | number;
  };
}

// Number field configuration
export interface NumberFieldConfig {
  id: string;
  type: 'number';
  label: string;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  unit?: string;           // e.g., "degrees", "meters"
  helpText?: string;
}

// Date field configuration
export interface DateFieldConfig {
  id: string;
  type: 'date';
  label: string;
  required?: boolean;
}

// Union type for all built-in field configs
export type BuiltInFieldConfig = 
  | PhotoFieldSchema
  | TextFieldConfig
  | TextAreaFieldConfig
  | DropdownFieldConfig
  | BooleanFieldConfig
  | NumberFieldConfig
  | DateFieldConfig;

// Section configuration for built-in forms
export interface BuiltInFormSection {
  id: string;
  title: string;
  description?: string;
  fields: BuiltInFieldConfig[];
}

// Complete form schema for a built-in property form
export interface BuiltInFormSchema {
  formId: string;                           // e.g., 'property_rear'
  formTitle: string;                        // e.g., 'Property Rear'
  sections: BuiltInFormSection[];           // Built-in sections
  defaultSectionOrder?: string[];           // Default order of section IDs
}

// Helper type for form data - generic key-value storage
export type FormDataValues = Record<string, any>;

// Helper type for photo data - maps field IDs to photo arrays
export interface DisplayablePhotoData {
  id?: string;
  url?: string;
  dataUrl?: string;
  timestamp?: number;
  displayId?: string;
  file?: File;
}

export type PhotoDataValues = Record<string, DisplayablePhotoData[]>;

// Props interface for the DynamicFormRenderer
export interface DynamicFormRendererProps {
  schema: BuiltInFormSchema;                // The form schema
  formData: FormDataValues;                 // Current form values
  photos: PhotoDataValues;                  // Current photo data
  
  // Callbacks
  onFormDataChange: (fieldId: string, value: any) => void;
  onPhotosChange: (fieldId: string, photos: DisplayablePhotoData[]) => void;
  onSubmit?: (e: React.FormEvent) => void;
  
  // Editor customization props
  customFields?: FieldSchema[];             // Custom fields from editor
  hiddenFields?: string[];                  // Field IDs to hide
  hiddenSections?: string[];                // Section IDs to hide
  sectionOrder?: string[];                  // Custom section order
  builtInFieldOrder?: string[];             // Custom field order within sections
  
  // Navigation
  navigationButtons?: {
    previous?: {
      label: string;
      onClick: () => void;
    };
    next?: {
      label: string;
      onClick: () => void;
    };
  };
  
  // Gallery
  onGalleryOpen?: (fieldId: string) => void;
  onGalleryClose?: () => void;
  selectedGallery?: string | null;
  
  // Styling
  className?: string;
  formTitle?: string;                       // Override schema title
  formIcon?: React.ReactNode;               // Icon to show next to title
}

// Helper to check if a field is a photo field
export function isPhotoField(field: BuiltInFieldConfig): field is PhotoFieldSchema {
  return field.type === 'photo';
}

// Helper to check if a field is conditional
export function isConditionalField(field: BuiltInFieldConfig): boolean {
  return 'conditionalOn' in field && field.conditionalOn !== undefined;
}

// Helper to evaluate if a conditional field should be shown
export function shouldShowConditionalField(
  field: BuiltInFieldConfig,
  formData: FormDataValues
): boolean {
  if (!isConditionalField(field)) return true;
  
  const photoField = field as PhotoFieldSchema;
  if (!photoField.conditionalOn) return true;
  
  const { fieldId, value } = photoField.conditionalOn;
  return formData[fieldId] === value;
}
